import React, { useState, useEffect, useCallback } from 'react';
import { useGridCopyMenu } from './gridCommon';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';
import {
  signalLabel, signalColor, signalBg,
  thStyle, tdStyle, tabActiveStyle, filterStyle, paramBoxStyle, paramTitleStyle,
  previewBaseColDefs,
  type PreviewOrder, type SimSignal, type SimVote, type SimResult, type BaseAmtInfo, type QuantStrategyInfo,
} from './quantCommon';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface StrategyConfig {
  ma: { fastPeriod: number; slowPeriod: number; tolerance: number };
  rsi: { period: number; oversold: number; overbought: number };
  bb: { period: number; stdDev: number };
  macd: { fast: number; slow: number; signal: number };
  momentum: { lookback: number; threshold: number };
  meanRev: { period: number; zScore: number };
  analysisYears: number;
  fromDate: string;
  toDate: string;
  enabledStrategies: string[];
}

const getDefaultDates = () => {
  const to = new Date();
  const from = new Date();
  from.setFullYear(from.getFullYear() - 1);
  return {
    fromDate: from.toISOString().split('T')[0],
    toDate: to.toISOString().split('T')[0],
  };
};

const defaultConfig: StrategyConfig = {
  ma: { fastPeriod: 15, slowPeriod: 30, tolerance: 0.00015 },
  rsi: { period: 14, oversold: 30, overbought: 70 },
  bb: { period: 20, stdDev: 2.0 },
  macd: { fast: 12, slow: 26, signal: 9 },
  momentum: { lookback: 10, threshold: 0.02 },
  meanRev: { period: 20, zScore: 2.0 },
  analysisYears: 1,
  ...getDefaultDates(),
  enabledStrategies: ['1', '2', '3', '4', '5', '6'],
};

const QuantSimulate: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const { selectedPfId, canOrder } = useAuth();   // canOrder: 주문 권한 없으면 주문 생성 버튼 숨김
  const axiosInstance = getMainAxios();

  const [strategyList, setStrategyList] = useState<QuantStrategyInfo[]>([]);
  const [config, setConfig] = useState<StrategyConfig>({ ...defaultConfig });
  const [simResult, setSimResult] = useState<SimResult | null>(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simError, setSimError] = useState<string | null>(null);
  const [simPreviewOrders, setSimPreviewOrders] = useState<PreviewOrder[]>([]);
  const [simPreviewApplied, setSimPreviewApplied] = useState(false);
  const [baseAmtInfo, setBaseAmtInfo] = useState<BaseAmtInfo | null>(null);
  const [savedRunId, setSavedRunId] = useState<number | null>(null);

  // 전략 목록 로드 (daily only)
  const fetchStrategies = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/v1/quant/strategies', { params: { type: 'daily' } });
      setStrategyList(res.data || []);
    } catch (err) {
      console.error('전략 목록 조회 실패:', err);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/v1/quant/config');
      if (res.data) setConfig(prev => ({ ...prev, ...res.data, fromDate: prev.fromDate, toDate: prev.toDate }));
    } catch (err) {
      console.error('설정 조회 실패:', err);
    }
  }, []);

  useEffect(() => { fetchStrategies(); fetchConfig(); }, [fetchStrategies, fetchConfig]);

  const buildRequest = (saveOrders: boolean, saveResult: boolean) => ({
    pfId: selectedPfId,
    analysisYears: config.analysisYears,
    fromDate: config.fromDate,
    toDate: config.toDate,
    orderQuantity: 100,
    saveOrders,
    saveResult,
    enabledStrategies: config.enabledStrategies,
    ma: { FastPeriod: config.ma.fastPeriod, SlowPeriod: config.ma.slowPeriod, Tolerance: config.ma.tolerance },
    rsi: { Period: config.rsi.period, Oversold: config.rsi.oversold, Overbought: config.rsi.overbought },
    bb: { Period: config.bb.period, StdDev: config.bb.stdDev },
    macd: { Fast: config.macd.fast, Slow: config.macd.slow, Signal: config.macd.signal },
    momentum: { Lookback: config.momentum.lookback, Threshold: config.momentum.threshold },
    meanRev: { Period: config.meanRev.period, ZScore: config.meanRev.zScore },
  });

  const runSimulation = async () => {
    if (!selectedPfId) return;
    setSimLoading(true);
    setSimError(null);
    setSimResult(null);
    setSimPreviewOrders([]);
    setSimPreviewApplied(false);
    setSavedRunId(null);
    try {
      const res = await axiosInstance.post('/api/v1/quant/simulate', buildRequest(false, false), { timeout: 60000 });
      setSimResult(res.data);
    } catch (err: any) {
      const msg = err.code === 'ECONNABORTED' ? '시간 초과 (60초)' : (err.response?.data?.error || err.message || '시뮬레이션 실패');
      setSimError(msg);
    } finally {
      setSimLoading(false);
    }
  };

  const saveResult = async () => {
    if (!selectedPfId || !simResult) return;
    setSimLoading(true);
    try {
      const res = await axiosInstance.post('/api/v1/quant/simulate', buildRequest(false, true), { timeout: 60000 });
      const runId = res.data?.runId;
      if (runId) {
        setSavedRunId(runId);
        alert(`시뮬레이션 결과 저장 완료 (run #${runId}). 이력 화면에서 조회할 수 있습니다.`);
      } else {
        alert('저장 실패: runId 없음');
      }
    } catch (err: any) {
      alert('저장 실패: ' + (err.response?.data?.error || err.message));
    } finally {
      setSimLoading(false);
    }
  };

  const simGeneratePreview = async () => {
    if (!selectedPfId || !simResult?.orderDate) return;
    if (selectedPfId === 0) { alert('주문은 개별 계좌를 선택해야 합니다.'); return; }
    setSimLoading(true);
    setSimError(null);
    setSimPreviewOrders([]);
    setSimPreviewApplied(false);
    try {
      await axiosInstance.post('/api/v1/quant/simulate', buildRequest(true, false), { timeout: 60000 });
      const res = await axiosInstance.post('/api/v1/quant/generate-orders', null, {
        params: { pfId: selectedPfId, d: simResult.orderDate, orderQuantity: 100, preview: true },
      });
      const orders: PreviewOrder[] = (res.data.orders || []).map((o: PreviewOrder) => ({ ...o, selected: true }));
      setSimPreviewOrders(orders);
      if (res.data.baseAmtInfo) setBaseAmtInfo(res.data.baseAmtInfo);
      if (orders.length === 0) alert('생성 가능한 주문이 없습니다.');
    } catch (err: any) {
      const msg = err.code === 'ECONNABORTED' ? '시간 초과 (60초)' : (err.response?.data?.error || err.message || '예상주문 생성 실패');
      setSimError(msg);
    } finally {
      setSimLoading(false);
    }
  };

  const simApplyOrders = async () => {
    if (!selectedPfId || !simResult?.orderDate) return;
    const selected = simPreviewOrders.filter(o => o.selected);
    if (selected.length === 0) { alert('선택된 종목이 없습니다.'); return; }
    if (!confirm(`${simResult.orderDate} 선택된 ${selected.length}건을 실제 주문으로 생성하시겠습니까?`)) return;
    setSimLoading(true);
    try {
      const prodIds = selected.map(o => o.prodId).join(',');
      const res = await axiosInstance.post('/api/v1/quant/generate-orders', null, {
        params: { pfId: selectedPfId, d: simResult.orderDate, orderQuantity: 100, prodIds },
      });
      const { orderCount, deleted, skipped } = res.data;
      setSimPreviewApplied(true);
      alert(`기존 ${deleted}건 삭제, ${orderCount}건 주문 생성${skipped > 0 ? `, ${skipped}건 매수불가 스킵` : ''}`);
    } catch (err: any) {
      alert('주문 적용 실패: ' + (err.response?.data?.error || err.message));
    } finally {
      setSimLoading(false);
    }
  };

  const toggleSimPreviewSelect = (prodId: string) => {
    setSimPreviewOrders(prev => prev.map(o => o.prodId === prodId ? { ...o, selected: !o.selected } : o));
  };
  const toggleSimPreviewSelectAll = () => {
    const allSelected = simPreviewOrders.every(o => o.selected);
    setSimPreviewOrders(prev => prev.map(o => ({ ...o, selected: !allSelected })));
  };

  const toggleStrategy = (key: string) => {
    setConfig(prev => ({
      ...prev,
      enabledStrategies: prev.enabledStrategies.includes(key)
        ? prev.enabledStrategies.filter(k => k !== key)
        : [...prev.enabledStrategies, key],
    }));
  };

  const updateParam = (group: keyof StrategyConfig, field: string, value: number) => {
    setConfig(prev => ({
      ...prev,
      [group]: { ...(prev[group] as any), [field]: value },
    }));
  };

  const renderParamInput = (label: string, value: number, onChange: (v: number) => void, step?: number) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <label style={{ fontSize: '11px', color: '#374151', whiteSpace: 'nowrap', minWidth: '60px' }}>{label}</label>
      <input type="number" value={value} onChange={e => onChange(parseFloat(e.target.value) || 0)} step={step ?? 1}
        style={{ width: '70px', padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }} />
    </div>
  );

  const renderSimResult = () => {
    if (!simResult) return null;
    const { strategies, signals: sigs, votes } = simResult;
    const byProd = new Map<string, SimSignal[]>();
    for (const s of sigs) {
      if (!byProd.has(s.prodId)) byProd.set(s.prodId, []);
      byProd.get(s.prodId)!.push(s);
    }
    return (
      <div style={{ overflowX: 'auto' }}>
        <div style={{ padding: '8px 10px', fontSize: '11px', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
          분석기간: {simResult.fromDate} ~ {simResult.toDate}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ ...thStyle, color: '#1e40af' }}>종목</th>
              <th style={{ ...thStyle, color: '#1e40af' }}>종가</th>
              {strategies.map(s => <th key={s} style={{ ...thStyle, color: '#1e40af' }}>{s}</th>)}
              <th style={{ ...thStyle, color: '#1e40af' }}>결정</th>
              <th style={{ ...thStyle, color: '#1e40af' }}>비중</th>
            </tr>
          </thead>
          <tbody>
            {votes.map(v => {
              const prodSigs = byProd.get(v.prodId) || [];
              return (
                <tr key={v.prodId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{v.prodNm || v.prodId}</div>
                    <div style={{ color: '#9ca3af', fontSize: '10px' }}>{v.prodId}</div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                    {prodSigs[0]?.lastClose?.toLocaleString() ?? '-'}
                  </td>
                  {strategies.map(sName => {
                    const sig = prodSigs.find(s => s.strategyName === sName);
                    if (!sig) return <td key={sName} style={{ ...tdStyle, textAlign: 'center' }}>-</td>;
                    return (
                      <td key={sName} style={{ ...tdStyle, textAlign: 'center', backgroundColor: signalBg(sig.signal) }}>
                        <div style={{ fontWeight: 700, color: signalColor(sig.signal), fontSize: '11px' }}>
                          {signalLabel(sig.signal)}
                        </div>
                        <div style={{ fontSize: '9px', color: '#9ca3af' }}>{(sig.confidence * 100).toFixed(0)}%</div>
                      </td>
                    );
                  })}
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={{
                      fontWeight: 700, fontSize: '11px',
                      color: v.decision === 'BUY' ? '#ef4444' : v.decision === 'SELL' ? '#3b82f6' : '#6b7280',
                    }}>{v.decision}</span>
                    <div style={{ fontSize: '9px', color: '#9ca3af' }}>B:{v.buyCount} S:{v.sellCount} H:{v.holdCount}</div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
                    {v.decision !== 'HOLD' ? `${v.wei}%` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {simLoading && <div style={{ marginBottom: '10px', fontSize: '11px', color: '#9ca3af' }}>로딩 중...</div>}

      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#1e40af' }}>전략 파라미터 (일봉 시뮬레이션)</h3>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={() => setConfig({ ...defaultConfig, ...getDefaultDates() })} style={filterStyle}>초기화</button>
            <button onClick={runSimulation} disabled={simLoading}
              style={{ ...tabActiveStyle, backgroundColor: simLoading ? '#9ca3af' : '#3b82f6', cursor: simLoading ? 'not-allowed' : 'pointer' }}>
              {simLoading ? '분석 중...' : '시뮬레이션 실행'}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
          {renderParamInput('분석기간(년)', config.analysisYears, v => {
            const to = new Date(config.toDate);
            const from = new Date(to);
            from.setFullYear(from.getFullYear() - v);
            setConfig(prev => ({ ...prev, analysisYears: v, fromDate: from.toISOString().split('T')[0] }));
          })}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '11px', color: '#374151', fontWeight: 600 }}>시작:</span>
            <input type="date" value={config.fromDate}
              onChange={e => setConfig(prev => ({ ...prev, fromDate: e.target.value }))}
              style={{ padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }} />
            <span style={{ fontSize: '11px', color: '#374151', fontWeight: 600, marginLeft: '4px' }}>종료:</span>
            <input type="date" value={config.toDate}
              onChange={e => setConfig(prev => ({ ...prev, toDate: e.target.value }))}
              style={{ padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }} />
          </div>
          <span style={{ color: '#d1d5db' }}>|</span>
          <span style={{ fontSize: '11px', color: '#374151', fontWeight: 600 }}>활성전략:</span>
          {strategyList.filter(s => s.enabled).map(s => (
            <button key={s.code} onClick={() => toggleStrategy(s.code)}
              title={s.description ?? ''}
              style={{
                padding: '3px 8px', borderRadius: '3px', fontSize: '11px', cursor: 'pointer', border: 'none',
                backgroundColor: config.enabledStrategies.includes(s.code) ? '#3b82f6' : '#e5e7eb',
                color: config.enabledStrategies.includes(s.code) ? 'white' : '#6b7280',
              }}>
              {s.name}
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {config.enabledStrategies.includes('1') && (
            <div style={paramBoxStyle}>
              <div style={paramTitleStyle}>MA Cross</div>
              {renderParamInput('Fast', config.ma.fastPeriod, v => updateParam('ma', 'fastPeriod', v))}
              {renderParamInput('Slow', config.ma.slowPeriod, v => updateParam('ma', 'slowPeriod', v))}
              {renderParamInput('Tolerance', config.ma.tolerance, v => updateParam('ma', 'tolerance', v), 0.0001)}
            </div>
          )}
          {config.enabledStrategies.includes('2') && (
            <div style={paramBoxStyle}>
              <div style={paramTitleStyle}>RSI</div>
              {renderParamInput('Period', config.rsi.period, v => updateParam('rsi', 'period', v))}
              {renderParamInput('Oversold', config.rsi.oversold, v => updateParam('rsi', 'oversold', v))}
              {renderParamInput('Overbought', config.rsi.overbought, v => updateParam('rsi', 'overbought', v))}
            </div>
          )}
          {config.enabledStrategies.includes('3') && (
            <div style={paramBoxStyle}>
              <div style={paramTitleStyle}>Bollinger Band</div>
              {renderParamInput('Period', config.bb.period, v => updateParam('bb', 'period', v))}
              {renderParamInput('StdDev', config.bb.stdDev, v => updateParam('bb', 'stdDev', v), 0.1)}
            </div>
          )}
          {config.enabledStrategies.includes('4') && (
            <div style={paramBoxStyle}>
              <div style={paramTitleStyle}>MACD</div>
              {renderParamInput('Fast', config.macd.fast, v => updateParam('macd', 'fast', v))}
              {renderParamInput('Slow', config.macd.slow, v => updateParam('macd', 'slow', v))}
              {renderParamInput('Signal', config.macd.signal, v => updateParam('macd', 'signal', v))}
            </div>
          )}
          {config.enabledStrategies.includes('5') && (
            <div style={paramBoxStyle}>
              <div style={paramTitleStyle}>Momentum</div>
              {renderParamInput('Lookback', config.momentum.lookback, v => updateParam('momentum', 'lookback', v))}
              {renderParamInput('Threshold', config.momentum.threshold, v => updateParam('momentum', 'threshold', v), 0.01)}
            </div>
          )}
          {config.enabledStrategies.includes('6') && (
            <div style={paramBoxStyle}>
              <div style={paramTitleStyle}>Mean Reversion</div>
              {renderParamInput('Period', config.meanRev.period, v => updateParam('meanRev', 'period', v))}
              {renderParamInput('Z-Score', config.meanRev.zScore, v => updateParam('meanRev', 'zScore', v), 0.1)}
            </div>
          )}
        </div>
      </div>

      {simResult && (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
          {renderSimResult()}
          <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: '#6b7280' }}>
              {simResult.votes.filter(v => v.decision !== 'HOLD').length}개 종목 주문 가능
              {simResult.orderDate && <span style={{ marginLeft: '6px' }}>→ 주문일: {simResult.orderDate}</span>}
              {savedRunId && <span style={{ marginLeft: '8px', color: '#16a34a', fontWeight: 600 }}>저장됨 (run #{savedRunId})</span>}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={saveResult} disabled={simLoading || savedRunId != null}
                style={{
                  padding: '6px 16px', borderRadius: '3px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  backgroundColor: (simLoading || savedRunId != null) ? '#9ca3af' : '#16a34a', color: 'white',
                }}>
                {savedRunId != null ? '저장 완료' : '결과 저장'}
              </button>
              {canOrder && (
              <button onClick={simGeneratePreview} disabled={simLoading || simPreviewApplied}
                style={{
                  padding: '6px 16px', borderRadius: '3px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  backgroundColor: (simLoading || simPreviewApplied) ? '#9ca3af' : '#f59e0b', color: 'white',
                }}>
                {simLoading ? '처리 중...' : simPreviewApplied ? '주문 적용 완료' : '예상주문생성'}
              </button>
              )}
            </div>
          </div>
        </div>
      )}

      {simPreviewOrders.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ backgroundColor: '#fffbeb', border: '2px solid #f59e0b', borderBottom: 'none', borderRadius: '8px 8px 0 0', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#92400e' }}>
                예상주문 미리보기 ({simPreviewOrders.length}건, 선택 {simPreviewOrders.filter(o => o.selected).length}건)
              </span>
              {baseAmtInfo && (
                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                  계산기준({baseAmtInfo.bsDate}): D+2 {baseAmtInfo.n2TotAmt.toLocaleString()}원 → 매수가능 {baseAmtInfo.buyableAmt.toLocaleString()}원
                </div>
              )}
            </div>
            {simPreviewApplied ? (
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#22c55e' }}>주문 적용 완료</span>
            ) : (
              <button onClick={simApplyOrders}
                disabled={simLoading || simPreviewOrders.filter(o => o.selected).length === 0}
                style={{
                  padding: '6px 16px', borderRadius: '3px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  backgroundColor: (simLoading || simPreviewOrders.filter(o => o.selected).length === 0) ? '#9ca3af' : '#ef4444', color: 'white',
                }}>
                {simLoading ? '처리 중...' : `주문적용 (${simPreviewOrders.filter(o => o.selected).length}건)`}
              </button>
            )}
          </div>
          <div className="ag-theme-quartz" style={{ height: Math.min(150 + simPreviewOrders.length * 42, 400), width: '100%', border: '2px solid #f59e0b', borderTop: '1px solid #fde68a', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
            <AgGridReact<PreviewOrder> {...copyMenu.gridProps}
              rowData={simPreviewOrders}
              columnDefs={[
                {
                  headerName: '', width: 40, pinned: 'left',
                  headerComponent: () => {
                    const allSelected = simPreviewOrders.every(o => o.selected);
                    return <input type="checkbox" checked={allSelected} onChange={toggleSimPreviewSelectAll} />;
                  },
                  cellRenderer: (p: any) => (
                    <input type="checkbox" checked={p.data.selected ?? false}
                      onChange={() => toggleSimPreviewSelect(p.data.prodId)} />
                  ),
                  sortable: false,
                },
                ...previewBaseColDefs,
              ]}
              defaultColDef={{ resizable: true, sortable: true, filter: false }}
              getRowId={p => p.data.prodId}
            />
          </div>
        </div>
      )}

      {simError && (
        <div style={{ backgroundColor: '#fee2e2', border: '2px solid #ef4444', borderRadius: '8px', padding: '16px', marginBottom: '20px', color: '#991b1b', fontSize: '12px' }}>
          시뮬레이션 오류: {simError}
        </div>
      )}

      {!simResult && !simLoading && !simError && (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
          파라미터를 설정하고 '시뮬레이션 실행' 버튼을 클릭하세요.
        </div>
      )}
      {copyMenu.menu}
    </div>
  );
};

export default QuantSimulate;
